import React, { useState, useEffect } from 'react';
import './profile.css';
import { getUserData, updateUserInitials, updateUserDefaultContact, fetchAllUsers, getCompanySettings, updateCompanySettings, fetchRetailCustomerSetting, updateRetailCustomerSetting, fetchChoices } from './api';
import CreateUserForm from './CreateUserForm';
import EditUserForm from './EditUserForm';
import DeleteUserModal from './DeleteUserModal';
import QuickBooksIntegration from './QuickBooksIntegration';
import ChoiceManager from './ChoiceManager';
import QuickBooksServiceMapping from './QuickBooksServiceMapping';
import DuplicateCustomerReport from './customers/DuplicateCustomerReport';

const Profile = ({ handleLogout, onClose }) => {
  const [user, setUser] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [initials, setInitials] = useState('');
  const [defaultContact, setDefaultContact] = useState('');
  const [filterJobsByDefaultContact, setFilterJobsByDefaultContact] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);
  const [error, setError] = useState('');
  const [contactError, setContactError] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showDeleteUser, setShowDeleteUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [activeAdminTab, setActiveAdminTab] = useState('users');
  const [activeSettingsTab, setActiveSettingsTab] = useState('profile');
  const [retailCustomerName, setRetailCustomerName] = useState('Retail');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [contactChoices, setContactChoices] = useState([]);
  const [invoiceSettings, setInvoiceSettings] = useState({
    company_name: '',
    company_address: '',
    company_city: '',
    company_phone: '',
    company_email: '',
    company_logo_url: '',
    payment_terms: '',
    invoice_footer_text: '',
    invoice_contact_text: '',
    paid_invoice_number: '',
  });
  const [invoiceSettingsLoading, setInvoiceSettingsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getUserData();
        setUser(response.data);
        setInitials(response.data.initials || '');
        setDefaultContact(response.data.default_contact || '');
        setFilterJobsByDefaultContact(!!response.data.filter_jobs_by_default_contact);
        
        // Fetch contact choices
        const choicesData = await fetchChoices();
        if (choicesData.contact) {
          setContactChoices(choicesData.contact);
        }
        
        // If user is admin, fetch all users for the admin panel
        if (response.data.is_admin) {
          try {
            const usersResponse = await fetchAllUsers();
            setUsers(usersResponse);
          } catch (error) {
            console.error('Error fetching users:', error);
          }
        }
        
        // Fetch retail customer setting only for admin users
        if (response.data.is_admin) {
          await fetchRetailSetting();
          await fetchInvoiceSettings();
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchData();
  }, []);

  const fetchRetailSetting = async () => {
    try {
      const data = await fetchRetailCustomerSetting();
      setRetailCustomerName(data.retail_customer_name || 'Retail');
    } catch (error) {
      console.error('Error fetching retail customer setting:', error);
    }
  };

  const updateRetailSetting = async () => {
    setSettingsLoading(true);
    try {
      await updateRetailCustomerSetting(retailCustomerName);
      setError('');
      alert('Retail customer setting updated successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update retail customer setting');
      console.error('Error updating retail customer setting:', err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleInitialsSubmit = async (e) => {
    e.preventDefault();
    if (initials.length > 3) {
      setError('Initials must be 3 characters or less');
      return;
    }
    try {
      const updatedUser = await updateUserInitials(initials);
      setUser(updatedUser);
      setIsEditing(false);
      setError('');
    } catch (error) {
      console.error('Error updating initials:', error);
      setError('Failed to update initials');
    }
  };

  // Hide the "Saved" note after a moment
  useEffect(() => {
    if (!contactSaved) return;
    const timeoutId = setTimeout(() => setContactSaved(false), 2000);
    return () => clearTimeout(timeoutId);
  }, [contactSaved]);

  // Default contact settings save as soon as they change
  const saveDefaultContactSettings = async (changes) => {
    const previous = { defaultContact, filterJobsByDefaultContact };
    if ('default_contact' in changes) setDefaultContact(changes.default_contact);
    if ('filter_jobs_by_default_contact' in changes) setFilterJobsByDefaultContact(changes.filter_jobs_by_default_contact);
    setContactSaving(true);
    setContactSaved(false);
    setContactError('');
    try {
      const updatedUser = await updateUserDefaultContact(changes);
      setUser(updatedUser);
      setDefaultContact(updatedUser.default_contact || '');
      setFilterJobsByDefaultContact(!!updatedUser.filter_jobs_by_default_contact);
      setContactSaved(true);
    } catch (error) {
      console.error('Error updating default contact:', error);
      setDefaultContact(previous.defaultContact);
      setFilterJobsByDefaultContact(previous.filterJobsByDefaultContact);
      setContactError('Failed to save. Please try again.');
    } finally {
      setContactSaving(false);
    }
  };
 
  const handleUserCreated = async () => {
    setShowCreateUser(false);
    // Refresh users list
    await refreshUsers();
  };

  const fetchInvoiceSettings = async () => {
    try {
      const settings = await getCompanySettings();
      setInvoiceSettings(settings);
    } catch (error) {
      console.error('Error fetching invoice settings:', error);
    }
  };

  const updateInvoiceSettings = async () => {
    try {
      setInvoiceSettingsLoading(true);
      setError('');
      
      await updateCompanySettings(invoiceSettings);
      alert('Invoice settings updated successfully!');
    } catch (err) {
      setError('Failed to update invoice settings');
      console.error('Error updating invoice settings:', err);
    } finally {
      setInvoiceSettingsLoading(false);
    }
  };

  const handleInvoiceSettingChange = (field, value) => {
    setInvoiceSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUserUpdated = async () => {
    setShowEditUser(false);
    setSelectedUser(null);
    // Refresh users list
    await refreshUsers();
  };

  const handleUserDeleted = async () => {
    setShowDeleteUser(false);
    setSelectedUser(null);
    // Refresh users list
    await refreshUsers();
  };

  const refreshUsers = async () => {
    try {
      const usersResponse = await fetchAllUsers();
      setUsers(usersResponse);
    } catch (error) {
      console.error('Error refreshing users:', error);
    }
  };

  const handleEditUser = (userItem) => {
    setSelectedUser(userItem);
    setShowEditUser(true);
  };

  const handleDeleteUser = (userItem) => {
    setSelectedUser(userItem);
    setShowDeleteUser(true);
  };

  const logout = () => {
    console.log('Removing token from local storage');
    localStorage.removeItem('authToken');
    handleLogout();
    window.location.reload();
  };

  const renderProfileSection = () => {
    const sortedContactChoices = [...contactChoices].sort((a, b) => a.order - b.order);
    const defaultContactName = defaultContact
      ? (contactChoices.find(c => c.value === defaultContact)?.display_name || defaultContact)
      : '';

    return (
      <div className="profile-section">
        <h3>Profile Settings</h3>

        <div className="profile-card">
          <div className="profile-row">
            <span className="profile-row-label">Username</span>
            <span className="profile-row-value">{user.username}</span>
          </div>
          <div className="profile-row">
            <span className="profile-row-label">Email</span>
            <span className="profile-row-value">{user.email || 'Not set'}</span>
          </div>
          <div className="profile-row">
            <span className="profile-row-label">Initials</span>
            {isEditing ? (
              <form className="profile-row-control" onSubmit={handleInitialsSubmit}>
                <input
                  type="text"
                  value={initials}
                  onChange={(e) => setInitials(e.target.value.toUpperCase())}
                  maxLength={3}
                  placeholder="ABC"
                  className="profile-initials-input"
                  autoFocus
                />
                <button type="submit" className="profile-btn profile-btn-primary">Save</button>
                <button
                  type="button"
                  className="profile-btn"
                  onClick={() => {
                    setIsEditing(false);
                    setInitials(user.initials || '');
                    setError('');
                  }}
                >
                  Cancel
                </button>
              </form>
            ) : (
              <div className="profile-row-control">
                <span className="profile-row-value">{user.initials || 'Not set'}</span>
                <button className="profile-btn" onClick={() => setIsEditing(true)}>
                  Edit
                </button>
              </div>
            )}
          </div>
          {isEditing && error && <p className="profile-inline-error">{error}</p>}
        </div>

        <h4 className="profile-subheading">Job Defaults</h4>
        <div className="profile-card">
          <div className="profile-row">
            <div className="profile-row-text">
              <label className="profile-row-label" htmlFor="profile-default-contact">Default Contact</label>
              <span className="profile-row-hint">Automatically selected when you create a new job</span>
            </div>
            <select
              id="profile-default-contact"
              className="profile-select"
              value={defaultContact}
              onChange={(e) => saveDefaultContactSettings({ default_contact: e.target.value })}
              disabled={contactSaving}
            >
              <option value="">No default contact</option>
              {sortedContactChoices.map(choice => (
                <option key={choice.value} value={choice.value}>
                  {choice.display_name}
                </option>
              ))}
              {defaultContact && !contactChoices.some(c => c.value === defaultContact) && (
                <option value={defaultContact}>{defaultContact}</option>
              )}
            </select>
          </div>

          <div className={`profile-row ${!defaultContact ? 'profile-row-disabled' : ''}`}>
            <div className="profile-row-text">
              <span className="profile-row-label" id="profile-filter-jobs-label">Filter Job List to Default Contact</span>
              <span className="profile-row-hint">
                {defaultContact
                  ? `The job list opens showing only ${defaultContactName}'s jobs. You can still pick other contacts from the job list filter.`
                  : 'Choose a default contact to turn this on.'}
              </span>
            </div>
            <label className="profile-switch">
              <input
                type="checkbox"
                role="switch"
                aria-labelledby="profile-filter-jobs-label"
                checked={!!defaultContact && filterJobsByDefaultContact}
                disabled={!defaultContact || contactSaving}
                onChange={(e) => saveDefaultContactSettings({ filter_jobs_by_default_contact: e.target.checked })}
              />
              <span className="profile-switch-slider" />
            </label>
          </div>

          {(contactSaving || contactSaved || contactError) && (
            <p className={`profile-save-status ${contactError ? 'error' : ''}`}>
              {contactError || (contactSaving ? 'Saving...' : 'Saved')}
            </p>
          )}
        </div>

        <button className="logout-btn" onClick={logout}>
          Logout
        </button>
      </div>
    );
  };

  const renderAdminSection = () => {
    if (!user.is_admin) {
      return null;
    }

    return (
      <div className="admin-section">
        <h3>Admin Settings</h3>
        
        {/* Admin Tabs */}
        <div className="admin-tabs">
          <button 
            className={`admin-tab-button ${activeAdminTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveAdminTab('users')}
          >
            User Management
          </button>
          <button 
            className={`admin-tab-button ${activeAdminTab === 'choices' ? 'active' : ''}`}
            onClick={() => setActiveAdminTab('choices')}
          >
            Choice Management
          </button>
          <button
            className={`admin-tab-button ${activeAdminTab === 'duplicates' ? 'active' : ''}`}
            onClick={() => setActiveAdminTab('duplicates')}
          >
            Duplicate Customers
          </button>
        </div>

        {/* Users Tab */}
        {activeAdminTab === 'users' && (
          <div className="admin-content">
            <h4>User Management</h4>
            <button 
              className="create-user-btn"
              onClick={() => setShowCreateUser(true)}
            >
              Create New User
            </button>
            
            {users.length > 0 && (
              <div className="users-list">
                <h5>All Users</h5>
                <div className="users-grid">
                  {users.map((userItem) => (
                    <div key={userItem.id} className="user-card">
                      <div className="user-info">
                        <strong>{userItem.username}</strong>
                        {userItem.is_admin && <span className="admin-badge">Admin</span>}
                      </div>
                      <div className="user-details">
                        <p>Email: {userItem.email}</p>
                        {userItem.initials && <p>Initials: {userItem.initials}</p>}
                        <p>Name: {userItem.first_name} {userItem.last_name}</p>
                      </div>
                      <div className="user-actions">
                        <button 
                          className="edit-user-btn"
                          onClick={() => handleEditUser(userItem)}
                        >
                          Edit
                        </button>
                        <button 
                          className="delete-user-btn"
                          onClick={() => handleDeleteUser(userItem)}
                          disabled={userItem.id === user.id}
                          title={userItem.id === user.id ? "Cannot delete your own account" : "Delete user"}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Choices Tab */}
        {activeAdminTab === 'choices' && (
          <div className="admin-content">
            <ChoiceManager />
          </div>
        )}

        {/* Duplicate Customers Tab */}
        {activeAdminTab === 'duplicates' && (
          <div className="admin-content">
            <DuplicateCustomerReport onOpenCustomer={onClose} />
          </div>
        )}
      </div>
    );
  };

  const renderQuickBooksSection = () => {
    if (!user.is_admin) {
      return null;
    }

    return (
      <div className="quickbooks-section">
        <h3>QuickBooks Settings</h3>
        
        <div className="quickbooks-tabs">
          <button 
            className={`quickbooks-tab-button ${activeSettingsTab === 'integration' ? 'active' : ''}`}
            onClick={() => setActiveSettingsTab('integration')}
          >
            Integration
          </button>
          <button 
            className={`quickbooks-tab-button ${activeSettingsTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveSettingsTab('settings')}
          >
            Settings
          </button>
          <button 
            className={`quickbooks-tab-button ${activeSettingsTab === 'invoice' ? 'active' : ''}`}
            onClick={() => setActiveSettingsTab('invoice')}
          >
            Invoice Settings
          </button>
          <button 
            className={`quickbooks-tab-button ${activeSettingsTab === 'service-mapping' ? 'active' : ''}`}
            onClick={() => setActiveSettingsTab('service-mapping')}
          >
            Service Mapping
          </button>
        </div>

        {activeSettingsTab === 'integration' && (
          <div className="quickbooks-content">
            <QuickBooksIntegration />
          </div>
        )}

        {activeSettingsTab === 'settings' && (
          <div className="quickbooks-content">
            <div className="setting-group">
              <label className="setting-label">
                Retail Customer Name:
                <input
                  type="text"
                  value={retailCustomerName}
                  onChange={(e) => setRetailCustomerName(e.target.value)}
                  className="setting-input"
                  placeholder="Retail"
                />
              </label>
              <p className="setting-description">
                This is the customer name that will be used in QuickBooks for all retail invoices.
                The actual customer name from your app will be set as the billing address.
              </p>
              <button 
                className="setting-save-btn"
                onClick={updateRetailSetting}
                disabled={settingsLoading}
              >
                {settingsLoading ? 'Saving...' : 'Save Setting'}
              </button>
              {error && <div className="error-message">{error}</div>}
            </div>
          </div>
        )}

        {activeSettingsTab === 'service-mapping' && (
          <div className="quickbooks-content">
            <QuickBooksServiceMapping />
          </div>
        )}

        {activeSettingsTab === 'invoice' && (
          <div className="quickbooks-content">
            <div className="invoice-settings">
              <h4>Company Information</h4>
              
              <div className="setting-group">
                <label className="setting-label">
                  Company Name:
                  <input
                    type="text"
                    value={invoiceSettings.company_name}
                    onChange={(e) => handleInvoiceSettingChange('company_name', e.target.value)}
                    className="setting-input"
                    placeholder="Paragon Wheel Repair"
                  />
                </label>
              </div>

              <div className="setting-group">
                <label className="setting-label">
                  Street Address:
                  <input
                    type="text"
                    value={invoiceSettings.company_address}
                    onChange={(e) => handleInvoiceSettingChange('company_address', e.target.value)}
                    className="setting-input"
                    placeholder="123 Main Street"
                  />
                </label>
              </div>

              <div className="setting-group">
                <label className="setting-label">
                  City, State, ZIP:
                  <input
                    type="text"
                    value={invoiceSettings.company_city}
                    onChange={(e) => handleInvoiceSettingChange('company_city', e.target.value)}
                    className="setting-input"
                    placeholder="Your City, ST 12345"
                  />
                </label>
              </div>

              <div className="setting-row">
                <div className="setting-group">
                  <label className="setting-label">
                    Phone Number:
                    <input
                      type="tel"
                      value={invoiceSettings.company_phone}
                      onChange={(e) => handleInvoiceSettingChange('company_phone', e.target.value)}
                      className="setting-input"
                      placeholder="(555) 123-4567"
                    />
                  </label>
                </div>

                <div className="setting-group">
                  <label className="setting-label">
                    Email Address:
                    <input
                      type="email"
                      value={invoiceSettings.company_email}
                      onChange={(e) => handleInvoiceSettingChange('company_email', e.target.value)}
                      className="setting-input"
                      placeholder="info@paragonwheelrepair.com"
                    />
                  </label>
                </div>
              </div>

              <div className="setting-group">
                <label className="setting-label">
                  Logo URL:
                  <input
                    type="url"
                    value={invoiceSettings.company_logo_url}
                    onChange={(e) => handleInvoiceSettingChange('company_logo_url', e.target.value)}
                    className="setting-input"
                    placeholder="https://example.com/logo.png"
                  />
                </label>
                <p className="setting-description">
                  Optional: URL to your company logo image
                </p>
              </div>

              <h4>Invoice Text</h4>

              <div className="setting-group">
                <label className="setting-label">
                  Payment Terms:
                  <textarea
                    value={invoiceSettings.payment_terms}
                    onChange={(e) => handleInvoiceSettingChange('payment_terms', e.target.value)}
                    className="setting-textarea"
                    rows="3"
                    placeholder="Payment is due upon receipt of this invoice."
                  />
                </label>
              </div>

              <div className="setting-group">
                <label className="setting-label">
                  Footer Text:
                  <textarea
                    value={invoiceSettings.invoice_footer_text}
                    onChange={(e) => handleInvoiceSettingChange('invoice_footer_text', e.target.value)}
                    className="setting-textarea"
                    rows="2"
                    placeholder="Thank you for your business!"
                  />
                </label>
              </div>

              <div className="setting-group">
                <label className="setting-label">
                  Contact Information:
                  <textarea
                    value={invoiceSettings.invoice_contact_text}
                    onChange={(e) => handleInvoiceSettingChange('invoice_contact_text', e.target.value)}
                    className="setting-textarea"
                    rows="2"
                    placeholder="Questions? Contact us at (555) 123-4567 or email info@paragonwheelrepair.com"
                  />
                </label>
              </div>

              <div className="setting-group">
                <label className="setting-label">
                  Paid Invoice Number:
                  <input
                    type="text"
                    value={invoiceSettings.paid_invoice_number}
                    onChange={(e) => handleInvoiceSettingChange('paid_invoice_number', e.target.value)}
                    className="setting-input"
                    placeholder="00000"
                  />
                  <small className="setting-description">
                    Invoice number used for paid work orders (e.g., "00000", "PAID", etc.)
                  </small>
                </label>
              </div>

              <button 
                className="setting-save-btn"
                onClick={updateInvoiceSettings}
                disabled={invoiceSettingsLoading}
              >
                {invoiceSettingsLoading ? 'Saving...' : 'Save Invoice Settings'}
              </button>
              {error && <div className="error-message">{error}</div>}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="profile">
      <h2 className='profileheader'>User Settings</h2>
      
      {/* Profile Section */}
      {renderProfileSection()}

      {/* Admin Settings Section */}
      {renderAdminSection()}

      {/* QuickBooks Settings Section */}
      {renderQuickBooksSection()}

      {/* Create User Modal */}
      {showCreateUser && (
        <CreateUserForm
          onUserCreated={handleUserCreated}
          onCancel={() => setShowCreateUser(false)}
        />
      )}

      {/* Edit User Modal */}
      {showEditUser && selectedUser && (
        <EditUserForm
          userId={selectedUser.id}
          onUserUpdated={handleUserUpdated}
          onCancel={() => {
            setShowEditUser(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Delete User Modal */}
      {showDeleteUser && selectedUser && (
        <DeleteUserModal
          user={selectedUser}
          onUserDeleted={handleUserDeleted}
          onCancel={() => {
            setShowDeleteUser(false);
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
};

export default Profile;
